/**
 * Hand Annotation Engine
 *
 * Analyzes played hands and produces coaching annotations
 * linked back to Poker Academy lessons.
 */

export interface HandAnnotation {
  pattern: string;
  lessonId: string;
  titleEs: string;
  titleEn: string;
  messageEs: string;
  messageEn: string;
}

export interface HandSummary {
  position: string; // "UTG" | "MP" | "CO" | "BTN" | "SB" | "BB"
  holeCards: string[]; // ["As", "Kh"]
  actions: { phase: string; action: string; amount?: number }[];
  potSize: number;
  bb: number;
  won: boolean;
  netResult: number;
  boardCards?: string[];
}

// ---------------------------------------------------------------------------
// Annotation catalog
// ---------------------------------------------------------------------------

export const ANNOTATIONS: HandAnnotation[] = [
  {
    pattern: "folded_in_position",
    lessonId: "2-1",
    titleEs: "Foldear en posicion tardia",
    titleEn: "Folded in late position",
    messageEs:
      "Estabas en posicion tardia (BTN/CO), donde puedes jugar mas manos. Revisa como aprovechar la ventaja posicional.",
    messageEn:
      "You were in late position (BTN/CO), where you can play more hands. Review how to leverage positional advantage.",
  },
  {
    pattern: "limped_preflop",
    lessonId: "2-5",
    titleEs: "Limpeo preflop",
    titleEn: "Limped preflop",
    messageEs:
      "Igualaste la ciega grande en vez de subir. Si la mano es lo suficientemente buena para jugar, normalmente es mejor subir.",
    messageEn:
      "You called the big blind instead of raising. If the hand is good enough to play, it's usually better to raise.",
  },
  {
    pattern: "no_cbet",
    lessonId: "3-2",
    titleEs: "Sin apuesta de continuacion",
    titleEn: "No continuation bet",
    messageEs:
      "Subiste preflop pero no apostaste en el flop. A menudo deberias seguir apostando para mantener la iniciativa.",
    messageEn:
      "You raised preflop but checked the flop. You should often keep betting to maintain initiative.",
  },
  {
    pattern: "called_big_bet",
    lessonId: "4-2",
    titleEs: "Call a apuesta grande",
    titleEn: "Called a large bet",
    messageEs:
      "Igualaste una apuesta superior al bote. Asegurate de tener las pot odds correctas antes de pagar apuestas grandes.",
    messageEn:
      "You called a bet larger than the pot. Make sure you have the right pot odds before calling large bets.",
  },
  {
    pattern: "folded_blinds_easily",
    lessonId: "2-4",
    titleEs: "Fold facil en las ciegas",
    titleEn: "Easy fold in the blinds",
    messageEs:
      "Foldeaste en la ciega grande ante una subida minima. Con una subida tan pequena, tienes buenas odds para defender.",
    messageEn:
      "You folded in the big blind to a min-raise. With such a small raise, you have good odds to defend.",
  },
  {
    pattern: "small_bet_wet_board",
    lessonId: "5-1",
    titleEs: "Apuesta pequena en board mojado",
    titleEn: "Small bet on a wet board",
    messageEs:
      "Apostaste menos de 1/3 del bote en un flop con muchos draws. En boards conectados, apuestas mas grandes protegen mejor tu mano.",
    messageEn:
      "You bet less than 1/3 pot on a draw-heavy flop. On connected boards, larger bets protect your hand better.",
  },
  {
    pattern: "played_trash_utg",
    lessonId: "3-1",
    titleEs: "Mano debil desde UTG",
    titleEn: "Weak hand from UTG",
    messageEs:
      "Jugaste una mano debil desde primera posicion. Desde UTG, solo deberias jugar el top 12-15% de manos.",
    messageEn:
      "You played a weak hand from early position. From UTG, you should only play the top 12-15% of hands.",
  },
  {
    pattern: "won_big_pot",
    lessonId: "2-1",
    titleEs: "Gran bote ganado",
    titleEn: "Big pot won",
    messageEs:
      "Ganaste un bote grande — mas de 10x la ciega grande. Buen trabajo extrayendo valor.",
    messageEn:
      "You won a big pot — more than 10x the big blind. Great job extracting value.",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LATE_POSITIONS = ["BTN", "CO"];
const EARLY_POSITIONS = ["UTG", "UTG+1"];

/** Rough hand-strength heuristic (for mock analysis). */
function isTrashHand(cards: string[]): boolean {
  if (cards.length < 2) return false;
  const r1 = cards[0].slice(0, -1);
  const r2 = cards[1].slice(0, -1);
  const suited = cards[0].slice(-1) === cards[1].slice(-1);

  const RANK_ORDER = "23456789TJQKA";
  const i1 = RANK_ORDER.indexOf(r1);
  const i2 = RANK_ORDER.indexOf(r2);
  const hi = Math.max(i1, i2);
  const lo = Math.min(i1, i2);

  // Pairs are never "trash"
  if (r1 === r2) return false;
  // Both below 9 and not suited connector
  if (hi < 7 && lo < 5) return true;
  // Disconnected low cards
  if (hi < 6 && !suited) return true;
  return false;
}

function hasFlushDraw(board: string[]): boolean {
  if (board.length < 3) return false;
  const suits: Record<string, number> = {};
  for (const c of board) {
    const s = c.slice(-1);
    suits[s] = (suits[s] || 0) + 1;
  }
  return Object.values(suits).some((n) => n >= 2);
}

function hasStraightDraw(board: string[]): boolean {
  if (board.length < 3) return false;
  const ORDER = "A23456789TJQKA";
  const indices = board.map((c) => ORDER.indexOf(c.slice(0, -1))).sort(
    (a, b) => a - b,
  );
  for (let i = 0; i < indices.length - 1; i++) {
    if (indices[i + 1] - indices[i] <= 2) return true;
  }
  return false;
}

function isWetBoard(board: string[]): boolean {
  return hasFlushDraw(board) || hasStraightDraw(board);
}

// ---------------------------------------------------------------------------
// Main analysis
// ---------------------------------------------------------------------------

export function analyzeHand(hand: HandSummary): HandAnnotation[] {
  const results: HandAnnotation[] = [];
  const ann = (pattern: string) =>
    ANNOTATIONS.find((a) => a.pattern === pattern);

  const preflopActions = hand.actions.filter((a) => a.phase === "preflop");
  const flopActions = hand.actions.filter((a) => a.phase === "flop");

  const foldedPreflop = preflopActions.some((a) => a.action === "fold");
  const calledPreflop = preflopActions.some((a) => a.action === "call");
  const raisedPreflop = preflopActions.some(
    (a) => a.action === "raise" || a.action === "bet",
  );

  // 1. Folded in late position preflop
  if (foldedPreflop && LATE_POSITIONS.includes(hand.position)) {
    const a = ann("folded_in_position");
    if (a) results.push(a);
  }

  // 2. Limped preflop (called BB instead of raising)
  if (calledPreflop && !raisedPreflop && preflopActions[0]?.action === "call") {
    const a = ann("limped_preflop");
    if (a) results.push(a);
  }

  // 3. No c-bet (raised preflop, checked flop)
  if (
    raisedPreflop &&
    flopActions.length > 0 &&
    flopActions.every((a) => a.action === "check")
  ) {
    const a = ann("no_cbet");
    if (a) results.push(a);
  }

  // 4. Called a large bet (> pot)
  for (const act of hand.actions) {
    if (
      act.action === "call" &&
      act.amount != null &&
      act.amount > hand.potSize &&
      act.phase !== "preflop"
    ) {
      const a = ann("called_big_bet");
      if (a) results.push(a);
      break;
    }
  }

  // 5. Folded in BB to a min-raise
  if (
    hand.position === "BB" &&
    foldedPreflop &&
    preflopActions.length === 1
  ) {
    const a = ann("folded_blinds_easily");
    if (a) results.push(a);
  }

  // 6. Small bet on wet board
  if (hand.boardCards && hand.boardCards.length >= 3) {
    const flopBoard = hand.boardCards.slice(0, 3);
    for (const act of flopActions) {
      if (
        (act.action === "bet" || act.action === "raise") &&
        act.amount != null &&
        act.amount < hand.potSize / 3 &&
        isWetBoard(flopBoard.map((c) => c))
      ) {
        const a = ann("small_bet_wet_board");
        if (a) results.push(a);
        break;
      }
    }
  }

  // 7. Played trash from UTG
  if (
    EARLY_POSITIONS.includes(hand.position) &&
    !foldedPreflop &&
    isTrashHand(hand.holeCards)
  ) {
    const a = ann("played_trash_utg");
    if (a) results.push(a);
  }

  // 8. Won big pot (positive reinforcement)
  if (hand.won && hand.potSize > 10 * hand.bb) {
    const a = ann("won_big_pot");
    if (a) results.push(a);
  }

  return results;
}
